import Box from '@mui/material/Box';
import type { FC } from 'react';
import { Outlet, useLocation } from 'react-router';
import SecuritySidebar from '../components/security/SecuritySidebar';
import { SECURITY_DASHBOARD_HREF } from '../config/securityNav';
import { getEdrModuleIdFromPath } from '../constants/edrSubscriptionModules';
import { EdrPreviewBannerSlot } from '../pages/security/edr/preview/EdrPreviewBannerSlot';
import { WazuhScopeProvider } from '../hooks/useWazuhScope';

/**
 * Security module shell: nested sidebar + child route outlet.
 * Only mounted under `/mindtime/security/*` so the extended sidebar stays scoped to Security.
 * SOC skips the nested sidebar (Mindtime Security sibling).
 *
 * Scroll contract:
 * - Outer Box fills its parent height exactly and clips overflow so children
 *   cannot push past the viewport.
 * - Sidebar column is fixed-width, fixed-height (`100%`), and lets its own
 *   internal menu scroll if needed without affecting the page.
 * - Main column owns the page scroll independently via `overflow: auto`.
 */
const SecurityLayout: FC = () => {
  const { pathname } = useLocation();
  const showNestedSidebar = !pathname.startsWith(SECURITY_DASHBOARD_HREF);
  const edrModuleId = getEdrModuleIdFromPath(pathname);

  return (
    <WazuhScopeProvider>
      <Box
        display="flex"
        flex={1}
        alignSelf="stretch"
        width="100%"
        minHeight={0}
        overflow="hidden"
        bgcolor="#F7FAFC"
      >
        {showNestedSidebar && (
          <Box
            flexShrink={0}
            width={220}
            height="100%"
            maxHeight="100%"
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <SecuritySidebar />
          </Box>
        )}
        <Box
          component="main"
          flex={1}
          minWidth={0}
          minHeight={0}
          height="100%"
          maxHeight="100%"
          overflow="auto"
          bgcolor="#F7FAFC"
        >
          {edrModuleId ? <EdrPreviewBannerSlot /> : null}
          <Outlet />
        </Box>
      </Box>
    </WazuhScopeProvider>
  );
};

export default SecurityLayout;
